import { PaymentOptionTabsConnected } from 'components/FinancingTabModal'
import { ModalConnected } from 'components/Phantom/_shop/Modals/Modal'
import { ProductTitleConnected } from 'components/Phantom/_shop/ProductTitle'
import { SelectorsConnected } from 'components/Phantom/_shop/Selectors'
import { SideNavConnected } from 'components/Phantom/_shop/SideNav'
import { StickyConnected } from 'components/Phantom/_shop/Sticky'
import { VisualsConnected } from 'components/Phantom/_shop/Visuals'
import { Button } from 'components/Phantom/Button'
import { Icon } from 'components/Phantom/Icon'
import { amShownRentalExperience } from 'events/amplitude'
import { useRouter } from 'next/navigation'
import { FC, useEffect } from 'react'
import { PaymentOption } from 'shop/Shop.types'
import { FragilePilotPrices } from 'shop/utils/fragilePilotPrices'
import { useIsUS } from 'stores/settings'
import { useHasAccessory, useSelectedVariant, useShippingText, useShopTotal, useShowAutopilotPricing, useTotalPriceFinancingNoMembership } from 'stores/shop'
import { useStore } from 'stores/StoreProvider'
import styles from './Shop.module.scss'

export const ConnectedShop: FC = () => {
	useEffect(() => {
		const intersectionObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					document.body.style.setProperty('--kustomer-height', '5.5rem')
				} else {
					document.body.style.removeProperty('--kustomer-height')
				}
			})
		})

		intersectionObserver.observe(document.getElementById('shop-side-nav'))

		return () => {
			intersectionObserver.disconnect()
			document.body.style.removeProperty('--kustomer-height')
		}
	}, [])

	const isFragile = useIsUS()

	useEffect(() => {
		if (isFragile) {
			amShownRentalExperience()
		}
	}, [isFragile])

	return (
		<main>
			<StickyConnected />
			<ModalConnected />

			<div
				className={styles.container}
				id="shop"
			>
				<div className={styles.container_inner}>
					<div className={styles.desktop_carousel_wrapper}>
						<VisualsConnected />
					</div>
					<div className={styles.sections}>
						<div className={styles.sticky_wrapper_desktop}>
							<PaymentOptionTabsConnected />
						</div>
						<ProductTitleConnected />
						<SelectorsConnected />
						<BottomStickyConnected />
					</div>
				</div>
				<div className={styles.side_nav_outer}>
					<div className={styles.side_nav_container}>
						<SideNavConnected />
					</div>
				</div>
			</div>
			{/*<Debug />*/}
		</main>
	)
}

const BottomStickyConnected = () => {
	const router = useRouter()

	const shippingText = useShippingText()
	const showAutopilotPricing = useShowAutopilotPricing()
	const paymentOption = useStore((state) => state.shop.paymentOption)
	const { price: totalPriceFinancingNoMembership, compare: totalPriceFinancingCompareNoMembership } = useTotalPriceFinancingNoMembership()
	const shopTotal = useShopTotal()
	const selectedVariant = useSelectedVariant()
	const hasAccessory = useHasAccessory()

	const selectModal = useStore((state) => state.shop.selectModal)
	const goingToCheckout = useStore((state) => state.shop.goingToCheckout)
	const checkout = useStore((state) => state.shop.checkout)

	const getPricingContent = () => {
		if (paymentOption === 'affirm') {
			const financingPrice = totalPriceFinancingNoMembership
			const financingCompare = totalPriceFinancingCompareNoMembership

			if (financingPrice === financingCompare) {
				return <>{financingPrice}/mo*</>
			}

			return (
				<>
					{financingPrice}/mo* <del>{financingCompare}/mo</del>
				</>
			)
		} else if (paymentOption === 'fragile') {
			return <>${FragilePilotPrices[selectedVariant]}/mo</>
		} else {
			const price = shopTotal.priceString
			const compare = shopTotal.comparePriceString

			if (price === compare) {
				return <>{price}</>
			}

			return (
				<>
					{price} <del>{compare}</del>
				</>
			)
		}
	}

	useEffect(() => {
		document.body.style.setProperty('--shortcut_bump', '7.3rem')
		return () => {
			document.body.style.setProperty('--shortcut_bump', '0px')
		}
	}, [])

	const membershipText = getMembershipText(paymentOption, hasAccessory)

	return (
		<div className={styles.bottom_sticky_wrapper}>
			<Button.Empty
				id={'pricing-button'}
				onClick={() => selectModal('financing-modal')}
				className={styles.pricing_button}
			>
				<span className={styles.big_chevron}>
					<Icon
						name={'ChevronDownLight'}
						color={'white'}
						size={16}
					/>
				</span>
				<span className={styles.pricing_button_interior}>
					<span className={styles.pricing_content}>
						{getPricingContent()}{' '}
						<Icon
							name={'ChevronDownLight'}
							color={'white'}
						/>
					</span>

					{showAutopilotPricing && (
						<span className={styles.membership_content}>
							<span className={styles.membership_text}>{membershipText}</span>
						</span>
					)}
				</span>
			</Button.Empty>
			<div className={styles.checkout_button}>
				<Button.White
					id={'checkout-button'}
					trackingData={{
						paymentMethod: paymentOption,
					}}
					loading={goingToCheckout}
					onClick={() => checkout('selection-summary', router)}
				>
					Checkout
				</Button.White>
				{shippingText && <p className={styles.shipping_text}>{shippingText}</p>}
			</div>
		</div>
	)
}

const getMembershipText = (paymentOption: PaymentOption, hasAccessory: boolean) => {
	if (paymentOption === 'affirm') {
		return '+ Autopilot plan'
	}

	if (paymentOption === 'fragile') {
		return 'includes Pod 4 + Autopilot'
	}

	return `includes Autopilot plan${hasAccessory ? ' + accessories' : ''}`
}
